/**
 * Company: SPHMedia
 * Description: This is the base page component for all BH pages
 */

import CASBlock from "@components/Blocks/CASBlock/CASBlock";
import AdsContainer from "@components/Blocks/Container/AdsContainer";
import { MainContainerNew } from "@components/Blocks/Container/MainContainerNew";
import CatfishAd from "@components/Elements/Advertisement/CatfishAd";
import { GoogleAdsSlotFactory } from "@components/Elements/Advertisement/helpers";
import PrestitialAd from "@components/Elements/Advertisement/PrestitialAd";
import { FooterNew } from "@components/Modules/FooterNew/FooterNew";
import { HeaderNew } from "@components/Modules/HeaderNew/HeaderNew";
import { pubPageAdTargeting } from "@config/ads";
import { AdsLb1 } from "@elements/Advertisement/variant/AdsLb";
import type { GADataProps } from "@elements/GAData/GAData";
import GAData from "@elements/GAData/GAData";
import type { MetaTagsProps } from "@elements/MetaTags/MetaTags";
import MetaTags from "@elements/MetaTags/MetaTags";
import { CAS_EI_MODEL_ID, IS_PLAYWRIGHT_TEST } from "@helper/getEnvVariables";
import { useNeuronPageViewPush } from "@hooks/useAnalyticsCall/useNeuronPageViewPush";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import { useGlobalMediaControl } from "@hooks/useGlobalMediaControl";
import { useHideInvisibleAds } from "@hooks/useHideInvisibleAds";
import useSendPermutiveIdentify from "@hooks/useSendPermutiveIdentify";
import {
  AdTargetingType,
  ArticleAdTargetKeys,
  PageAdTargetValue,
} from "@typings/Ads.d";
import { ReactElement } from "react";

export type AdsSettings = {
  isPrestitialEnabled: boolean | undefined;
  isCatfishEnabled: boolean | undefined;
  topOverlayImpressions: number;
  topOverlayValidity: number;
  isTaboolaEnabled: boolean | undefined;
};

export type BHArticleBasePageProps = {
  adsUniqueSectionName?: string;
  children?: (adsSettings: AdsSettings) => React.ReactNode;
  hasLb1Ads?: boolean;
  gaData?: GADataProps;
  metaTags?: MetaTagsProps;
  pageAdTargetValue?: PageAdTargetValue;
  gsChannels?: string[];
  articleSlotTarget?: AdTargetingType<ArticleAdTargetKeys, string>[];
  prestitialPageTargeting?: PageAdTargetValue;
  showGEBanner?: boolean;
};

/**
 * BHBasePage
 * Description: The base page component for all BH pages
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function BHArticleBasePage({
  adsUniqueSectionName,
  children,
  hasLb1Ads = true,
  gaData,
  metaTags,
  pageAdTargetValue,
  gsChannels,
  articleSlotTarget,
  prestitialPageTargeting,
  showGEBanner = true,
}: BHArticleBasePageProps): ReactElement {
  useNeuronPageViewPush();
  useSendPermutiveIdentify();
  const adsSettings: AdsSettings = useGlobalAdSetting();
  useGlobalMediaControl();
  useHideInvisibleAds();

  const pageAdTarget =
    pageAdTargetValue &&
    (pubPageAdTargeting[pageAdTargetValue] as AdTargetingType<
      string,
      PageAdTargetValue
    >);
  const prestitialPageAdTarget =
    prestitialPageTargeting &&
    (pubPageAdTargeting[prestitialPageTargeting] as AdTargetingType<
      string,
      PageAdTargetValue
    >);

  const lb1PageNumberSlotTarget: AdTargetingType<ArticleAdTargetKeys, string> =
    {
      key: ArticleAdTargetKeys.PAGE_NUMBER,
      value: "1",
    };

  return (
    <>
      {gaData && <GAData {...gaData} />}
      {metaTags && <MetaTags {...metaTags} />}
      <MainContainerNew>
        <>
          <AdsContainer className="flex justify-center border-b-[1px] border-black-900">
            {hasLb1Ads && (
              <AdsLb1
                uniqueSectionName={adsUniqueSectionName}
                {...(pageAdTargetValue && {
                  pageAdTargetType: pageAdTargetValue,
                })}
                hasStripeBg={false}
                wrapperStyle="mb-0"
                isArticlePage={true}
                gsChannels={gsChannels}
                articleSlotTarget={[
                  ...(articleSlotTarget ?? []),
                  lb1PageNumberSlotTarget,
                ]}
              />
            )}
          </AdsContainer>
          <HeaderNew
            showGEBanner={showGEBanner}
            hideScrollableSectionMenu={true}
          />
          {IS_PLAYWRIGHT_TEST !== "true" && (
            <PrestitialAd
              slot={GoogleAdsSlotFactory.prestitial(adsUniqueSectionName)}
              isPrestitialEnabled={adsSettings.isPrestitialEnabled}
              {...(prestitialPageAdTarget && {
                pubAdTargeting: [prestitialPageAdTarget],
              })}
              topOverlayImpressions={adsSettings.topOverlayImpressions}
              topOverlayValidity={adsSettings.topOverlayValidity}
              gsChannels={gsChannels}
              articleSlotTarget={articleSlotTarget?.slice(0, 2)}
            />
          )}
          {typeof children === "function" ? children(adsSettings) : children}
        </>
      </MainContainerNew>
      <FooterNew />
      {adsSettings.isCatfishEnabled && (
        <CatfishAd
          slot={GoogleAdsSlotFactory.catfish(adsUniqueSectionName)}
          isCatfishEnabled={adsSettings.isCatfishEnabled}
          {...(pageAdTarget && { pubAdTargeting: [pageAdTarget] })}
          gsChannels={gsChannels}
          articleSlotTarget={articleSlotTarget}
        />
      )}
      <CASBlock casId={CAS_EI_MODEL_ID} />
    </>
  );
}
